import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useQuery, useQueryClient } from "react-query";
import { userRequest } from "../../requestMethods";
import "./historique.css";

const monthLabels = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const buildYearOptions = (currentYear) =>
  Array.from({ length: 4 }, (_, idx) => currentYear - idx);

const normalizeRange = (start, end) => ({
  startDate: start.startOf("day").toISOString(),
  endDate: end.endOf("day").toISOString(),
});

const formatInputDate = (date) => dayjs(date).format("YYYY-MM-DD");

const fetchSales = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { startDate, endDate, page, limit } = params;
  const { data } = await userRequest.get("/sales", {
    params: { startDate, endDate, page, limit },
  });
  return data;
};

const fetchAllSales = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { startDate, endDate, total } = params;
  const limit = total || 1000;
  const { data } = await userRequest.get("/sales", {
    params: { startDate, endDate, page: 1, limit },
  });
  return data;
};

const fetchProducts = async () => {
  const { data } = await userRequest.get("/products");
  return data;
};

export const Historique = () => {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month();

  const initialRange = {
    startDate: formatInputDate(now.startOf("month")),
    endDate: formatInputDate(now.endOf("month")),
  };

  const [filters, setFilters] = useState(initialRange);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [activePreset, setActivePreset] = useState("thisMonth");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const queryClient = useQueryClient();

  const queryRange = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return { startDate: undefined, endDate: undefined };
    const normalized = normalizeRange(dayjs(filters.startDate), dayjs(filters.endDate));
    return normalized;
  }, [filters]);

  useEffect(() => {
    setPage(0);
  }, [filters, rowsPerPage]);

  const { data, isLoading, isFetching } = useQuery(
    [
      "historique-sales",
      {
        startDate: queryRange.startDate,
        endDate: queryRange.endDate,
        page: page + 1,
        limit: rowsPerPage,
      },
    ],
    fetchSales,
    { keepPreviousData: true }
  );

  const { data: productsData } = useQuery("historique-products", fetchProducts);

  const { data: allData, isFetching: isFetchingAll } = useQuery(
    [
      "historique-sales-all",
      {
        startDate: queryRange.startDate,
        endDate: queryRange.endDate,
        total: data?.totalSales,
      },
    ],
    fetchAllSales,
    {
      enabled:
        Boolean(data?.totalSales) &&
        Boolean(queryRange.startDate) &&
        Boolean(queryRange.endDate),
      keepPreviousData: true,
    }
  );

  const buyingPriceByRef = useMemo(() => {
    if (!productsData) return {};
    return productsData.reduce((acc, product) => {
      acc[product.ref] = product.buyingPrice;
      return acc;
    }, {});
  }, [productsData]);

  const flattenedRows = useMemo(() => {
    if (!data?.sales) return [];
    return data.sales.flatMap((sale) =>
      sale.products.map((product, idx) => {
        const finalPrice = product.unitPrice * product.quantity - (product.discount || 0);
        const buyingPrice = buyingPriceByRef[product.productRef];
        const benefit =
          finalPrice - (buyingPrice !== undefined ? buyingPrice * product.quantity : 0);
        return {
          id: `${sale._id}-${product.productRef}-${idx}`,
          saleId: sale._id,
          saleDate: sale.createdAt,
          productName: product.name,
          productRef: product.productRef,
          buyingPrice,
          unitPrice: product.unitPrice,
          discount: product.discount || 0,
          finalPrice,
          benefit,
          quantity: product.quantity,
          paymentMethod: sale.paymentMethod,
        };
      })
    );
  }, [data?.sales, buyingPriceByRef]);

  const allFlattenedRows = useMemo(() => {
    if (!allData?.sales) return [];
    return allData.sales.flatMap((sale) =>
      sale.products.map((product, idx) => {
        const finalPrice = product.unitPrice * product.quantity - (product.discount || 0);
        const buyingPrice = buyingPriceByRef[product.productRef];
        const benefit =
          finalPrice - (buyingPrice !== undefined ? buyingPrice * product.quantity : 0);
        return {
          id: `${sale._id}-${product.productRef}-all-${idx}`,
          saleId: sale._id,
          saleDate: sale.createdAt,
          productName: product.name,
          productRef: product.productRef,
          buyingPrice,
          unitPrice: product.unitPrice,
          discount: product.discount || 0,
          finalPrice,
          benefit,
          quantity: product.quantity,
          paymentMethod: sale.paymentMethod,
        };
      })
    );
  }, [allData?.sales, buyingPriceByRef]);

  const totalsSource = allFlattenedRows.length ? allFlattenedRows : flattenedRows;

  const totals = useMemo(
    () =>
      totalsSource.reduce(
        (acc, row) => {
          const quantity = Number(row.quantity) || 0;
          const unitPrice = Number(row.unitPrice) || 0;
          const discount = Number(row.discount) || 0;
          const finalPrice = Number(row.finalPrice) || 0;
          const buying = Number(row.buyingPrice) || 0;
          const benefit = Number(row.benefit) || finalPrice - buying * quantity;

          acc.selling += unitPrice * quantity;
          acc.discount += discount;
          acc.final += finalPrice;
          acc.benefit += benefit;
          acc.qty += quantity;
          return acc;
        },
        { selling: 0, discount: 0, final: 0, benefit: 0, qty: 0 }
      ),
    [totalsSource]
  );

  const yearOptions = buildYearOptions(currentYear);
  const totalPages = data?.totalPages ?? 1;

  const applyRange = (start, end) => {
    setFilters({
      startDate: formatInputDate(start),
      endDate: formatInputDate(end),
    });
    queryClient.invalidateQueries("historique-sales");
  };

  const handlePreset = (preset) => {
    const today = dayjs();
    const presets = {
      thisMonth: [today.startOf("month"), today.endOf("month")],
      thisYear: [today.startOf("year"), today.endOf("year")],
      last30: [today.subtract(29, "day"), today],
      last90: [today.subtract(89, "day"), today],
    };
    const range = presets[preset];
    if (range) {
      setActivePreset(preset);
      setSelectedYear(preset === "thisMonth" || preset === "thisYear" ? today.year() : null);
      setSelectedMonth(preset === "thisMonth" ? today.month() : null);
      applyRange(range[0], range[1]);
    }
  };

  const handleYearClick = (year) => {
    setActivePreset(null);
    setSelectedYear(year);
    setSelectedMonth(null);
    applyRange(dayjs().year(year).startOf("year"), dayjs().year(year).endOf("year"));
  };

  const handleMonthClick = (monthIndex) => {
    const yearForMonth = selectedYear || currentYear;
    const start = dayjs().year(yearForMonth).month(monthIndex).startOf("month");
    const end = dayjs().year(yearForMonth).month(monthIndex).endOf("month");
    if (yearForMonth === currentYear && monthIndex > currentMonth) return;
    setActivePreset(null);
    setSelectedYear(yearForMonth);
    setSelectedMonth(monthIndex);
    applyRange(start, end);
  };

  const handleCustomDateChange = (key, value) => {
    setActivePreset(null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePageChange = (direction) => {
    setPage((prev) => {
      const next = prev + direction;
      if (next < 0) return 0;
      if (data?.totalPages && next >= data.totalPages) return data.totalPages - 1;
      return next;
    });
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
  };

  const isMonthDisabled = (monthIndex) =>
    selectedYear === currentYear && monthIndex > currentMonth;

  return (
    <div className="historique-page">
      <div className="historique-header">
        <div>
          <h1>Historique des ventes</h1>
          <p className="muted">Visualize all sold products with detailed pricing.</p>
        </div>
        {isFetching && <span className="badge badge-loading">Updating...</span>}
      </div>

      <div className="filters-card">
        <div className="filter-block">
          <div className="filter-title">Quick presets</div>
          <div className="chip-row">
            <button
              className={`chip ${activePreset === "thisMonth" ? "active" : ""}`}
              onClick={() => handlePreset("thisMonth")}
            >
              This Month
            </button>
            <button
              className={`chip ${activePreset === "thisYear" ? "active" : ""}`}
              onClick={() => handlePreset("thisYear")}
            >
              This Year
            </button>
            <button
              className={`chip ${activePreset === "last30" ? "active" : ""}`}
              onClick={() => handlePreset("last30")}
            >
              Last 30 Days
            </button>
            <button
              className={`chip ${activePreset === "last90" ? "active" : ""}`}
              onClick={() => handlePreset("last90")}
            >
              Last 90 Days
            </button>
          </div>
        </div>

        <div className="filter-block">
          <div className="filter-title">Years</div>
          <div className="chip-row">
            {yearOptions.map((year) => (
              <button
                key={year}
                className={`chip ${selectedYear === year && selectedMonth === null ? "active" : ""}`}
                onClick={() => handleYearClick(year)}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-block">
          <div className="filter-title">Months ({selectedYear || currentYear})</div>
          <div className="month-grid">
            {monthLabels.map((label, idx) => (
              <button
                key={label}
                className={`chip ${selectedMonth === idx ? "active" : ""}`}
                onClick={() => handleMonthClick(idx)}
                disabled={isMonthDisabled(idx)}
                title={
                  isMonthDisabled(idx)
                    ? "Future months are disabled for the current year"
                    : `Filter to ${label}`
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-block">
          <div className="filter-title">Custom range</div>
          <div className="range-row">
            <label className="range-input">
              <span>Start</span>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleCustomDateChange("startDate", e.target.value)}
              />
            </label>
            <label className="range-input">
              <span>End</span>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleCustomDateChange("endDate", e.target.value)}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-header">
          <div>
            <h2>Sales history</h2>
            <p className="muted">
              {flattenedRows.length} product line{flattenedRows.length === 1 ? "" : "s"} loaded
              from page {page + 1} of {totalPages}.
            </p>
            <p className="muted">
              Totals cover all filtered pages{isFetchingAll ? " (updating…)" : ""}.
            </p>
          </div>
          <div className="pagination-controls">
            <label>
              Rows per page
              <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                {[5, 10, 15, 20, 30].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <div className="pager">
              <button onClick={() => handlePageChange(-1)} disabled={page === 0}>
                Prev
              </button>
              <span>
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(1)}
                disabled={data?.totalPages ? page + 1 >= data.totalPages : true}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="empty-state">Loading sales...</div>
        ) : flattenedRows.length === 0 ? (
          <div className="empty-state">
            No sales found for the selected dates. Try a different range.
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="historique-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Ref</th>
                  <th>Buying</th>
                  <th>Selling</th>
                  <th>Discount</th>
                  <th>Final</th>
                  <th>Benefit</th>
                  <th>Qty</th>
                  <th>Payment</th>
                </tr>
              </thead>
              <tbody>
                {flattenedRows.map((row) => (
                  <tr key={row.id}>
                    <td>{dayjs(row.saleDate).format("YYYY-MM-DD HH:mm")}</td>
                    <td className="cell-ellipsis">{row.productName}</td>
                    <td>{row.productRef}</td>
                    <td>{row.buyingPrice !== undefined ? `${row.buyingPrice} DA` : "—"}</td>
                    <td>{row.unitPrice} DA</td>
                    <td>{row.discount} DA</td>
                    <td>{row.finalPrice} DA</td>
                    <td>{row.benefit} DA</td>
                    <td>{row.quantity}</td>
                    <td className="capitalize">{row.paymentMethod}</td>
                  </tr>
                ))}
                <tr className="totals-row">
                  <td colSpan="4">Totals</td>
                  <td>{totals.selling} DA</td>
                  <td>{totals.discount} DA</td>
                  <td>{totals.final} DA</td>
                  <td>{totals.benefit} DA</td>
                  <td>{totals.qty}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Historique;

